import React from 'react';
import { Lightbulb, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from './ui/Modal';

interface UploadTipDialogProps {
  onClose: () => void;
  onDismiss: () => void;
}

/**
 * UploadTipDialog - Suggest creating templates for better extraction
 *
 * Shows when user has no templates and uploads a document
 * Encourages template creation to improve accuracy
 */
const UploadTipDialog: React.FC<UploadTipDialogProps> = ({ onClose, onDismiss }) => {
  const navigate = useNavigate();

  const handleCreateTemplate = () => {
    navigate('/uploaded-invoices/templates/new');
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={
        <>
          <span className="p-2 bg-yellow-500/20 rounded-lg">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
          </span>
          Improve Extraction Accuracy
        </>
      }
      description="Create a template for better results"
      footer={
        <>
          <button
            onClick={onDismiss}
            className="px-4 py-2 bg-stone-100 hover:bg-stone-200 dark:bg-coral-500/10 dark:hover:bg-coral-500/20 border border-stone-200 dark:border-coral-500/30 rounded-lg text-stone-700 dark:text-foreground-secondary text-sm transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={handleCreateTemplate}
            className="px-4 py-2 bg-coral-600 hover:bg-coral-700 dark:bg-coral-500 dark:hover:bg-coral-600 rounded-lg text-white font-medium text-sm transition-colors flex items-center justify-center gap-2"
          >
            Create Template
            <ArrowRight className="w-4 h-4" />
          </button>
        </>
      }
    >
        <div className="space-y-4">
          <p className="text-sm text-stone-700 dark:text-foreground-secondary">
            You don't have any templates yet. Creating a template helps the system:
          </p>

          <div className="space-y-2">
            <div className="flex items-start gap-2 text-sm">
              <span className="text-green-600 dark:text-green-400 flex-shrink-0">✓</span>
              <span className="text-stone-700 dark:text-foreground-secondary">Validate extracted fields automatically</span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <span className="text-green-600 dark:text-green-400 flex-shrink-0">✓</span>
              <span className="text-stone-700 dark:text-foreground-secondary">Catch missing required fields</span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <span className="text-green-600 dark:text-green-400 flex-shrink-0">✓</span>
              <span className="text-stone-700 dark:text-foreground-secondary">Improve accuracy for similar documents</span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <span className="text-green-600 dark:text-green-400 flex-shrink-0">✓</span>
              <span className="text-stone-700 dark:text-foreground-secondary">Learn patterns from your corrections</span>
            </div>
          </div>

          <div className="p-3 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-500/20">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              <strong>Tip:</strong> Upload a sample invoice, select the fields you need, and save.
              That's it! Future invoices from the same vendor will be validated automatically.
            </p>
          </div>
        </div>
    </Modal>
  );
};

export default UploadTipDialog;
